/**
 * Workspace Map — multi-layer location map.
 * Layers: Employee (current addr), Employee (permanent addr), Client, Vendor, Office.
 * Search filter, layer toggles, click marker for detailed bubble with address/phone/email.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Users, Briefcase, Building2, UserSquare2, Layers, Map as MapIcon } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon paths under bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LAYER_META = {
  employee_current: { label: "Employee · current", color: "#2563eb", icon: UserSquare2 },
  employee_permanent: { label: "Employee · permanent", color: "#7c3aed", icon: UserSquare2 },
  client: { label: "Clients", color: "#16a34a", icon: Briefcase },
  vendor: { label: "Vendors", color: "#ea580c", icon: Users },
  office: { label: "Offices", color: "#dc2626", icon: Building2 },
};

const ALL_LAYERS = Object.keys(LAYER_META);

function coloredIcon(color) {
  // Renders a simple pin SVG so each layer is visually distinct.
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='40' viewBox='0 0 28 40'><path d='M14 0C6.27 0 0 6.27 0 14c0 9.39 12.16 24.7 13.16 25.86a1 1 0 0 0 1.68 0C15.84 38.7 28 23.39 28 14 28 6.27 21.73 0 14 0z' fill='${color}' stroke='#fff' stroke-width='1.5'/><circle cx='14' cy='14' r='5' fill='#fff'/></svg>`;
  return L.divIcon({
    html: svg,
    className: "",
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

function FitToPins({ pins }) {
  const map = useMap();
  useEffect(() => {
    if (!pins.length) return;
    const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.2), { animate: true });
    }
  }, [pins, map]);
  return null;
}

export default function WorkspaceMap() {
  const { activeWorkspaceId } = useAuth();
  const [active, setActive] = useState(() => new Set(ALL_LAYERS));
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const layers = Array.from(active).join(",");
      const r = await api.get(`/workspaces/${activeWorkspaceId}/map-pins?layers=${layers || "none"}`);
      setData(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, active]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    if (!ql) return data.pins;
    return data.pins.filter((p) =>
      (p.label + " " + (p.sublabel || "") + " " + (p.meta?.address || "") + " " + (p.meta?.email || "")).toLowerCase().includes(ql)
    );
  }, [data, q]);

  const toggle = (l) => {
    setActive((s) => {
      const n = new Set(s);
      if (n.has(l)) n.delete(l); else n.add(l);
      return n;
    });
  };

  return (
    <div className="space-y-4" data-testid="map-page">
      <PageHeader
        title="Map"
        subtitle="All location-tagged data — employees, clients, vendors, offices — on a single map. Toggle layers, search, or click a pin for details."
        icon={MapIcon}
      />

      <Card className="rounded-none">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap" data-testid="map-controls">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input className="pl-9 h-9 rounded-none" placeholder="Search name, address, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="map-search" />
          </div>
          <span className="text-[10px] uppercase tracking-widest text-foreground/55 flex items-center gap-1"><Layers className="w-3 h-3" /> Layers</span>
          {ALL_LAYERS.map((l) => {
            const meta = LAYER_META[l];
            const on = active.has(l);
            const n = (data?.summary?.by_layer || {})[l] || 0;
            return (
              <button
                key={l}
                onClick={() => toggle(l)}
                className={`text-xs px-2.5 py-1.5 border rounded-none flex items-center gap-1.5 ${on ? "bg-foreground/5" : "opacity-40 hover:opacity-70"}`}
                style={on ? { borderColor: meta.color, color: meta.color } : { borderColor: "currentColor" }}
                data-testid={`map-layer-${l}`}
              >
                <span className="w-2.5 h-2.5 inline-block" style={{ background: meta.color }} />
                {meta.label}
                <span className="text-foreground/55 font-mono">{n}</span>
              </button>
            );
          })}
          {busy && <span className="text-[11px] text-foreground/55 ml-auto">Geocoding…</span>}
        </CardContent>
      </Card>

      <Card className="rounded-none overflow-hidden">
        <CardContent className="p-0">
          <div style={{ height: 600, width: "100%", position: "relative" }} data-testid="map-canvas">
            <MapContainer center={[20, 0]} zoom={2} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToPins pins={filtered} />
              {filtered.map((p) => (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={coloredIcon(LAYER_META[p.layer]?.color || "#666")}>
                  <Popup>
                    <div data-testid={`map-pin-${p.id}`} style={{ minWidth: 220 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{p.label}</div>
                      <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>{p.sublabel || LAYER_META[p.layer]?.label}</div>
                      {p.meta?.address && <div style={{ fontSize: 11, marginBottom: 3 }}><strong>Address:</strong> {p.meta.address}</div>}
                      {p.meta?.phone && <div style={{ fontSize: 11, marginBottom: 3 }}><strong>Phone:</strong> {p.meta.phone}</div>}
                      {p.meta?.email && <div style={{ fontSize: 11, marginBottom: 3 }}><strong>Email:</strong> {p.meta.email}</div>}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            {!busy && filtered.length === 0 && (
              <div className="absolute inset-0 bg-background/85 flex items-center justify-center pointer-events-none z-[400]" data-testid="map-empty">
                <div className="border border-border bg-card p-5 max-w-sm text-center pointer-events-auto space-y-2 shadow-lg">
                  <MapPin className="w-7 h-7 mx-auto text-foreground/40" />
                  <div className="text-sm font-semibold">No pins to show</div>
                  <p className="text-[11px] text-foreground/60">No locations match the current layers / search. Make sure employees, clients and vendors have an address — the first lookup of each address is geocoded via OpenStreetMap and cached.</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
